export type OperationsDependency = {
  status: "ready" | "not-ready" | "disabled" | "unavailable";
  code: string | null;
};

export type PlatformOperationsSnapshot = {
  generatedAt: string;
  status: "complete" | "partial";
  runtime: { version: string | null; buildNumber: string | null; environment: string | null };
  dependencies: { database: OperationsDependency; redis: OperationsDependency };
  traffic: {
    status: "available" | "no-samples" | "unavailable";
    scope: "current-api-process";
    windowMinutes: 15;
    maximumSamples: number;
    resetsOnRestart: true;
    requestCount: number | null;
    serverErrorCount: number | null;
    serverErrorRatePercent: number | null;
    p95LatencyMilliseconds: number | null;
  };
};

function object(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

function label(value: unknown): boolean {
  return value === null || (typeof value === "string" && /^[A-Za-z0-9._+-]{1,128}$/.test(value));
}

function dependency(value: unknown, name: "DATABASE" | "REDIS"): boolean {
  const item = object(value);
  return (item.status === "ready" && item.code === null)
    || (name === "REDIS" && item.status === "disabled" && item.code === null)
    || (item.status === "not-ready" && item.code === `${name}_NOT_READY`)
    || (item.status === "unavailable" && item.code === `${name}_UNAVAILABLE`);
}

function count(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isSnapshot(value: unknown): value is PlatformOperationsSnapshot {
  const snapshot = object(value);
  const runtime = object(snapshot.runtime);
  const dependencies = object(snapshot.dependencies);
  const traffic = object(snapshot.traffic);
  if (typeof snapshot.generatedAt !== "string" || snapshot.generatedAt.length > 40
    || !Number.isFinite(Date.parse(snapshot.generatedAt))
    || !["complete", "partial"].includes(String(snapshot.status))
    || !label(runtime.version) || !label(runtime.buildNumber) || !label(runtime.environment)
    || !dependency(dependencies.database, "DATABASE") || !dependency(dependencies.redis, "REDIS")
    || traffic.scope !== "current-api-process" || traffic.windowMinutes !== 15
    || !count(traffic.maximumSamples) || traffic.maximumSamples === 0 || traffic.resetsOnRestart !== true) return false;

  const partial = object(dependencies.database).status === "unavailable"
    || object(dependencies.redis).status === "unavailable" || traffic.status === "unavailable"
    || runtime.version === null || runtime.buildNumber === null || runtime.environment === null;
  if (snapshot.status !== (partial ? "partial" : "complete")) return false;
  if (traffic.status === "unavailable") return traffic.requestCount === null && traffic.serverErrorCount === null
    && traffic.serverErrorRatePercent === null && traffic.p95LatencyMilliseconds === null;
  if (!count(traffic.requestCount) || !count(traffic.serverErrorCount)
    || traffic.requestCount > traffic.maximumSamples || traffic.serverErrorCount > traffic.requestCount) return false;
  if (traffic.status === "no-samples") return traffic.requestCount === 0 && traffic.serverErrorCount === 0
    && traffic.serverErrorRatePercent === null && traffic.p95LatencyMilliseconds === null;
  return traffic.status === "available" && traffic.requestCount > 0
    && typeof traffic.serverErrorRatePercent === "number" && Number.isFinite(traffic.serverErrorRatePercent)
    && traffic.serverErrorRatePercent >= 0 && traffic.serverErrorRatePercent <= 100
    && count(traffic.p95LatencyMilliseconds);
}

function safeCorrelationId(value: unknown): string | null {
  return typeof value === "string" && /^[A-Za-z0-9_:-]{1,128}$/.test(value) ? value : null;
}

export async function getPlatformOperations(token: string, signal: AbortSignal): Promise<PlatformOperationsSnapshot> {
  const response = await fetch("/api/v1/platform-console/operations", {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    cache: "no-store",
    credentials: "omit",
    redirect: "error",
    signal
  });
  const body: unknown = await response.json().catch(() => null);
  if (response.ok && isSnapshot(body)) return body;

  const problem = object(body);
  const code = ["SESSION_INVALID", "INTERNAL_ERROR"].includes(String(problem.title))
    ? String(problem.title) : "PLATFORM_OPERATIONS_UNAVAILABLE";
  // Only diagnostic identifiers are retained; raw backend text never enters component state.
  throw Object.assign(new Error("Platform operations unavailable."), {
    status: response.ok ? 502 : response.status,
    problem: {
      title: code,
      correlationId: safeCorrelationId(response.headers.get("X-Correlation-Id"))
        ?? safeCorrelationId(problem.correlationId)
    }
  });
}
